import { Box, Card, CardContent, CardMedia, Chip, Link, Stack } from "@mui/material";
import { Typography } from "../atoms/Typography";
import { ProjectStatus } from "../../types/project";

interface ProjectCardProps {
  title: string;
  status: ProjectStatus;
  summary?: string;
  role?: string;
  techStack: string[];
  url: string;
  imageUrl?: string;
  isSmallScreen: boolean;
  disableBlur?: boolean;
}

export const ProjectCard = ({
  title,
  status,
  summary,
  role,
  techStack,
  url,
  imageUrl,
  isSmallScreen,
  disableBlur,
}: ProjectCardProps) => {
  return (
    <Card
      sx={{
        backgroundColor: "#1a2836",
        borderRadius: "10px",
        display: "flex",
        flexDirection: isSmallScreen ? "column" : "row",
        height: { xs: "auto", md: "300px" },
        mb: 4
      }}
    >
      <Box 
        sx={{ 
          position: 'relative',
          width: isSmallScreen ? "100%" : "50%",
          flexShrink: 0
        }}
      >
        <CardMedia
          component="img"
          image={imageUrl || "/placeholder.jpg"}
          alt={title}
          sx={{
            filter: status === ProjectStatus.ONGOING && !disableBlur ? "blur(3px)" : "none",
            transition: "filter 0.3s ease",
            width: "100%",
            height: isSmallScreen ? "250px" : "100%", // Increased height
            objectFit: "cover"
          }}
        />
        <Chip
          label={status}
          color={status === ProjectStatus.ONGOING ? "primary" : "success"}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1,
            backgroundColor: status === ProjectStatus.ONGOING ? "#0f7f9b" : "#4caf50",
            color: "white"
          }}
        />
      </Box>

      <CardContent sx={{
        width: isSmallScreen ? "100%" : "50%",
        color: "white",
        backgroundColor: "#1a2836",
        opacity: "0.9",
        p: 3,
        pl: isSmallScreen ? 3 : 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between"
      }}>
        <Box sx={{ overflow: "auto", flex: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontSize: {
                  xs: "25px",
                  sm: "25px",
                  md: "25px",
                  lg: "30px",
                  xl: "30px",
                },
                textAlign: "left"
              }}
            >
              {title}
            </Typography>
          </Box>

          {summary && (
            <Typography
              variant="h5"
              sx={{
                textAlign: "left",
                mb: 2,
                fontSize: {
                  xs: "15px",
                  sm: "15px",
                  md: "15px",
                  lg: "18px",
                  xl: "18px",
                }
              }}
            >
              {summary}
            </Typography>
          )}

          {role && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  textAlign: "left",
                  fontSize: {
                    xs: "18px",
                    sm: "18px",
                    md: "18px",
                    lg: "20px",
                    xl: "20px",
                  }
                }}
              >
                Role:
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  textAlign: "left",
                  fontSize: {
                    xs: "15px",
                    sm: "15px",
                    md: "15px",
                    lg: "18px",
                    xl: "18px",
                  }
                }}
              >
                {role}
              </Typography>
            </Box>
          )}

          {techStack && techStack.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  textAlign: "left",
                  fontSize: {
                    xs: "18px",
                    sm: "18px",
                    md: "18px",
                    lg: "20px",
                    xl: "20px",
                  }
                }}
              >
                Tech Stack:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {techStack.map((tech) => (
                  <Chip
                    key={tech}
                    label={tech}
                    size="small"
                    sx={{
                      backgroundColor: "#2c3e50",
                      color: "white"
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {url && (
          <Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="mouse-enlarge"
            sx={{
              color: "#61dafb",
              textDecoration: "none",
              alignSelf: "flex-start",
              '&:hover': {
                textDecoration: "underline"
              }
            }}
          >
            Visit Project →
          </Link>
        )}
      </CardContent>
    </Card>
  );
};